import React, { useEffect, useMemo, useState } from 'react';
import { I18n } from 'common/i18n';

import {
  FormatStyle,
  Expression,
  TableColumnFormat,
  ConditionalConfig,
  FontStyleTypes,
} from 'common/visualizations/views/agGridReact/types';
import { ViewColumn } from 'common/types/viewColumn';
import {
  AccordionContainerV2 as AccordionContainer,
  AccordionPaneV2 as AccordionPane,
} from 'common/components/Accordion';
import { ForgeIcon, ForgeButton } from '@tylertech/forge-react';
import { uniqueId, memoize } from 'lodash';
import {
  defaultConditionalConfig,
  defaultFontStyle,
  getColumnDataTypeInfo,
  getDataTypeName,
} from './helpers';
import DragDropContainer, { DragDropContainerType } from 'common/components/DragDropContainer';
import ConditionalTitleElement from 'common/authoring_workflow/components/panes/PresentationPane/AgGridUpdated/AgGridColumnFormatting/ConditionalTitleElement';
import ConditionalMainElement from 'common/authoring_workflow/components/panes/PresentationPane/AgGridUpdated/AgGridColumnFormatting/ConditionalMainElement';

const scope = 'shared.visualizations.panes.presentation.fields.ag_updated_column_format.conditional_options';

interface ConditionalOptionsProps {
  tableColumnFormat: TableColumnFormat;
  viewColumn: ViewColumn;
  onUpdateColumnFormat: (newColumnFormat: TableColumnFormat, columnName: string) => void;
}

const ConditionalOptions = (props: ConditionalOptionsProps) => {
  const { tableColumnFormat, viewColumn, onUpdateColumnFormat } = props;

  const accordionTitle = I18n.t('title', { scope });
  const addButtonLabel = I18n.t('add_button_label', { scope });

  const [conditionalConfig, setConditionalConfig] = useState<ConditionalConfig[]>(
    tableColumnFormat.conditionalConfig || [],
  );

  // Generate stable IDs for drag-drop tracking
  // Each config object gets a permanent ID that survives reordering
  const getStableId = useMemo(
    () =>
      memoize(
        (config: ConditionalConfig) => uniqueId('config-'),
        (config: ConditionalConfig) => config, // Use object reference as cache key
      ),
    [],
  );

  useEffect(() => {
    setConditionalConfig(tableColumnFormat.conditionalConfig || []);
  }, [tableColumnFormat]);

  const { dataType, isDateRenderType } = useMemo(() => {
    const dateTypeName = getDataTypeName(viewColumn);
    return getColumnDataTypeInfo(dateTypeName);
  }, [viewColumn]);

  const handleUpdateColumnFormat = (updatedConditionalConfig: ConditionalConfig[]) => {
    onUpdateColumnFormat(
      {
        ...tableColumnFormat,
        conditionalConfig: updatedConditionalConfig,
      },
      viewColumn.fieldName,
    );
  };

  const handleUpdateComplexStyle = (index: number, newStyle: FormatStyle) => {
    const updatedConditionalConfig = conditionalConfig.map((config, i) => {
      if (index === i) {
        return {
          ...config,
          style: newStyle,
        };
      }
      return config;
    });
    handleUpdateColumnFormat(updatedConditionalConfig);
  };

  const handleUpdateComplexExpression = (index: number, newExpression: Expression) => {
    const updatedConditionalConfig = conditionalConfig.map((config, i) => {
      if (index === i) {
        return {
          ...config,
          expression: newExpression,
        };
      }
      return config;
    });
    handleUpdateColumnFormat(updatedConditionalConfig);
  };

  const handleTextStyleChange = (index: number, textStyle: FontStyleTypes) => {
    const currentConditionalConfig = conditionalConfig[index];
    const style = currentConditionalConfig.style;
    const currentFontStyle = style.fontStyle || defaultFontStyle();
    if (!(textStyle in currentFontStyle)) currentFontStyle[textStyle] = false;
    const updatedStyle = {
      ...style,
      fontStyle: {
        ...currentFontStyle,
        [textStyle]: !currentFontStyle[textStyle],
      },
    } as FormatStyle;
    handleUpdateComplexStyle(index, updatedStyle);
  };

  const handleColorChange = (index: number, key: string, newColor: string) => {
    const currentConditionalConfig = conditionalConfig[index];
    const style = currentConditionalConfig.style;
    const updatedStyle = {
      ...style,
      [key]: newColor,
    } as FormatStyle;
    handleUpdateComplexStyle(index, updatedStyle);
  };

  const onConditionSelect = (newCondition: string, index?: number) => {
    index = index || 0;
    const currentConditionalConfig = conditionalConfig[index];
    const expression = currentConditionalConfig.expression;
    const updatedExpression = {
      ...expression,
      function: newCondition,
      arguments: {
        ...expression.arguments,
      },
    };
    handleUpdateComplexExpression(index, updatedExpression);
  };

  const onConditionValueChange = (value: string | number, key: string, index?: number) => {
    index = index || 0;
    const currentConditionalConfig = conditionalConfig[index];
    const expression = currentConditionalConfig.expression;
    const updatedExpression = {
      ...expression,
      arguments: {
        ...expression.arguments,
        [key]: value,
      },
    };
    handleUpdateComplexExpression(index, updatedExpression);
  };

  const onDateValueChange = (index: number, from: string, to: string) => {
    const currentConditionalConfig = conditionalConfig[index];
    const expression = currentConditionalConfig.expression;
    const updatedExpression = {
      ...expression,
      arguments: {
        ...expression.arguments,
        start: from,
        end: to,
      },
    };
    handleUpdateComplexExpression(index, updatedExpression);
  };

  const handleAddCondition = () => {
    const updatedConditionalConfig = [...conditionalConfig, defaultConditionalConfig(isDateRenderType)];
    setConditionalConfig(updatedConditionalConfig);
    const updatedColumnFormat = {
      ...tableColumnFormat,
      conditionalConfig: updatedConditionalConfig,
    };
    onUpdateColumnFormat(updatedColumnFormat, viewColumn.fieldName);
  };

  const handleDeleteCondition = (index: number) => {
    const updatedConditionalConfig = conditionalConfig.filter((_config, i) => i !== index);
    setConditionalConfig(updatedConditionalConfig);
    const updatedColumnFormat = {
      ...tableColumnFormat,
      conditionalConfig: updatedConditionalConfig,
    };
    onUpdateColumnFormat(updatedColumnFormat, viewColumn.fieldName);
  };

  const handleRearrangeConditions = (newConditionalConfig: ConditionalConfig[]) => {
    setConditionalConfig(newConditionalConfig);
    onUpdateColumnFormat(
      {
        ...tableColumnFormat,
        conditionalConfig: newConditionalConfig,
      },
      viewColumn.fieldName,
    );
  };

  const renderDraggableConditionalOptions = () => {
    const wrappedConditionalElements = conditionalConfig.map((config, index) => ({
      id: getStableId(config),
      dragAndDroppable: true,
      element: (
        <ConditionalTitleElement
          index={index}
          totalConditions={conditionalConfig.length}
          onDelete={() => handleDeleteCondition(index)}
        />
      ),
      mainElement: (
        <ConditionalMainElement
          config={config}
          index={index}
          viewColumn={viewColumn}
          dataType={dataType}
          onTextStyleChange={(textStyle: FontStyleTypes) => handleTextStyleChange(index, textStyle)}
          onColorChange={(key: string, newColor: string) => handleColorChange(index, key, newColor)}
          onConditionSelect={onConditionSelect}
          onConditionValueChange={onConditionValueChange}
          onDateValueChange={(from: string, to: string) => onDateValueChange(index, from, to)}
        />
      ),
      className: 'conditional-element-item',
      additionalItemProps: {
        'data-testid': `conditional-element-${index}`,
      },
      value: config,
    }));

    return (
      <DragDropContainer
        key={conditionalConfig.map((c) => getStableId(c)).join('-')}
        className={'conditional-config'}
        type={DragDropContainerType.CONDITIONS}
        items={conditionalConfig}
        iconName="drag_handle"
        onDrop={handleRearrangeConditions}
        childElements={wrappedConditionalElements}
      />
    );
  };

  return (
    <div className="control-wrapper">
      <AccordionContainer>
        <AccordionPane title={accordionTitle}>
          <div className="conditional-config-container">
            {renderDraggableConditionalOptions()}
            <ForgeButton
              className="add-condition-button"
              variant="outlined"
              aria-label={addButtonLabel}
              onClick={handleAddCondition}
            >
              <ForgeIcon name="add" />
              <span>{addButtonLabel}</span>
            </ForgeButton>
          </div>
        </AccordionPane>
      </AccordionContainer>
    </div>
  );
};

export default ConditionalOptions;
